import type { ReactNode } from "react";
import { cn } from "./_utils";

/**
 * Shell primitive for YAGNI's right/left-side conversation surfaces.
 *
 * Owns the shared chrome — atmospheric backdrop, header (identity pip + title
 * + subtitle + actions), optional status banner slot, and the body region.
 * The actual message list + composer live inside `children` (typically an
 * `InlineChat` or `ConversationPanel` in chromeless mode).
 *
 * Callers decide positioning: a fixed slide-in panel, an absolute right
 * strip, or a flex-column dock all pass their own outer container classes.
 * The shell only commits to internal layout (header / banner / body) and the
 * visual identity (fonts, spacing, separators, cream backdrop).
 *
 * @example
 * <ConversationSidebar title="Thread" actions={<CloseButton />}>
 *   <InlineChat conversationId={id} agentName="yagni" initialMessages={[]} />
 * </ConversationSidebar>
 * <ConversationSidebar
 *   tone="sidebar"
 *   title="Help & feedback"
 *   subtitle="Chat with @yagni"
 *   banner={<ConversationSidebarBanner tone="success" title="Marked resolved" />}
 * >
 *   <ConversationPanel parentType="feedback" parentId={userId} chromeless />
 * </ConversationSidebar>
 */
export interface ConversationSidebarProps {
  /** Display title. Rendered in Sora. */
  title: string;
  /** Optional one-line subtitle under the title. DM Sans, tertiary. */
  subtitle?: string;
  /**
   * Identity pip rendered at the leading edge of the title. Defaults to an
   * amber dot — the shared @yagni identity marker. Pass a custom node
   * (Sparkles icon, AgentIdentityDot for a specific agent role, etc.) for
   * per-surface variation without breaking the alignment contract.
   */
  icon?: ReactNode;
  /** Trailing header slot — typically Close, Collapse, or both IconButtons. */
  actions?: ReactNode;
  /**
   * Status banner rendered between header and body. Use for transient
   * context like "Goal created", "Marked as resolved", "Draft saved". Layout
   * does not reserve space when null.
   */
  banner?: ReactNode;
  /** Conversation body — messages + composer. */
  children: ReactNode;
  /** Forwarded to the outer <aside>. */
  className?: string;
  /**
   * Visual tone. `surface` (default) sits on the ambient field. `sidebar`
   * prints the dock (--print-bg) so it reads as distinct next to other
   * field-colored surfaces.
   */
  tone?: "surface" | "sidebar";
}

export function ConversationSidebar({
  title,
  subtitle,
  icon,
  actions,
  banner,
  children,
  className,
  tone = "surface",
}: ConversationSidebarProps) {
  const background =
    tone === "sidebar" ? "var(--print-bg)" : "var(--bg-base)";

  return (
    <aside
      className={cn(
        "relative flex h-full min-h-0 min-w-0 w-full flex-col overflow-hidden",
        className,
      )}
      style={{ background }}
    >
      {/* Atmospheric top bloom — a faint radial wash of amber pulls the eye
          to the header without committing to a loud brand stripe. Kept below
          the header content via z-index ordering. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-32 opacity-80"
        style={{
          background:
            "radial-gradient(120% 100% at 50% 0%, var(--accent-subtle) 0%, transparent 70%)",
        }}
      />

      <header className="relative shrink-0 px-5 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <span
              aria-hidden
              className="conversation-sidebar-pip mt-[6px] shrink-0"
            >
              {icon ?? (
                <span
                  className="block h-2 w-2 rounded-full"
                  style={{
                    background: "var(--accent)",
                    boxShadow:
                      "0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent)",
                  }}
                />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <h2
                className="truncate text-[15px] font-semibold leading-tight"
                style={{
                  color: "var(--text-primary)",
                  fontFamily: "var(--font-display)",
                  letterSpacing: "-0.005em",
                }}
              >
                {title}
              </h2>
              {subtitle ? (
                <p
                  className="mt-1 text-[12px] leading-snug"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>
          {actions ? (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          ) : null}
        </div>
        {/* Hairline separator with subtle amber fade at the edges — reads as
            a deliberate rule rather than a generic border. */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-px"
          style={{
            background:
              "linear-gradient(to right, transparent 0%, var(--border) 12%, var(--border) 88%, transparent 100%)",
          }}
        />
      </header>

      {banner ? (
        <div className="relative shrink-0 px-4 pt-3">{banner}</div>
      ) : null}

      <div className="relative flex-1 min-h-0 min-w-0">{children}</div>
    </aside>
  );
}

/**
 * Standard banner variants used inside ConversationSidebar's `banner` slot.
 * Kept co-located so every surface gets the same amber/green/subtle visual
 * treatment for "something happened" notices.
 */
export interface ConversationSidebarBannerProps {
  tone?: "success" | "accent" | "neutral";
  icon?: ReactNode;
  title: string;
  description?: string;
}

export function ConversationSidebarBanner({
  tone = "neutral",
  icon,
  title,
  description,
}: ConversationSidebarBannerProps) {
  const palette =
    tone === "success"
      ? {
          background: "color-mix(in srgb, var(--success) 10%, transparent)",
          border: "color-mix(in srgb, var(--success) 32%, transparent)",
          titleColor: "var(--success)",
        }
      : tone === "accent"
        ? {
            background: "var(--accent-muted)",
            border: "color-mix(in srgb, var(--accent) 32%, transparent)",
            titleColor: "var(--accent)",
          }
        : {
            background: "var(--print-bg)",
            border: "var(--print-border)",
            titleColor: "var(--text-primary)",
          };

  return (
    <div
      className="flex items-start gap-2 rounded-md px-3 py-2"
      style={{
        background: palette.background,
        border: `1px solid ${palette.border}`,
      }}
    >
      {icon ? (
        <span
          className="shrink-0"
          style={{ color: palette.titleColor, marginTop: 1 }}
        >
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div
          className="text-[12px] font-semibold"
          style={{ color: palette.titleColor }}
        >
          {title}
        </div>
        {description ? (
          <div
            className="mt-0.5 text-[11px] leading-snug"
            style={{ color: "var(--text-secondary)" }}
          >
            {description}
          </div>
        ) : null}
      </div>
    </div>
  );
}
